import { useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import TableBody from '@material-ui/core/TableBody';
import { Card, CardBody } from '@/shared/components/Card';
import { Table, TableRow, TableWrap, TableCell } from '@/shared/components/MaterialTableElements';
import { LoadingSpinner } from '@/shared/components/Loading';
import { Button } from '@/shared/components/Button';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';
import { useContentReports, useUpdateContentReportStatus, type ReportReason, type ReportStatus } from '@/services/content-reports';
import { formatDate } from '@/utils/helpers';

const STATUS_LABELS: Record<ReportStatus, string> = {
    open: 'Open',
    reviewed: 'Reviewed',
    action_taken: 'Action taken',
    dismissed: 'Dismissed',
};

const REASON_LABELS: Record<ReportReason, string> = {
    spam: 'Spam',
    harassment: 'Harassment',
    hate_speech: 'Hate speech',
    sexual_content: 'Sexual content',
    violence: 'Violence',
    self_harm: 'Self-harm',
    other: 'Other',
};

const STATUS_COLOURS: Record<ReportStatus, string> = {
    open: '#ffb52d',
    reviewed: '#8cbeff',
    action_taken: '#2dff8b',
    dismissed: '#adadad',
};

/**
 * Staff triage for member-reported chat messages.
 *
 * App Review Guideline 1.2 asks for "a mechanism to report offensive content
 * **and timely responses to concerns**". The report button shipped first; this
 * is the half that lets somebody actually respond. Before it, the only signal
 * staff got was a Slack line carrying ids and a reason — not the message.
 */
const ContentReports = () => {
    const [status, setStatus] = useState<ReportStatus | ''>('open');
    // Cursor pagination, so the trail of visited pages is kept to support "Back".
    const [cursors, setCursors] = useState<string[]>([]);

    const startAfter = cursors[cursors.length - 1];
    const { data: result, isLoading } = useContentReports({ status: status || undefined, startAfter });
    const { mutate: updateStatus, isPending: isSaving } = useUpdateContentReportStatus();

    const reports = result?.data?.reports ?? [];
    const nextCursor = result?.data?.nextCursor ?? null;

    const onFilterChange = (next: ReportStatus | '') => {
        setStatus(next);
        setCursors([]); // a new filter is a new result set — old cursors don't apply
    };

    return (
        <Container>
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Content reports</h3>
                    <h3 className="page-subhead subhead">Messages members have reported. Newest first.</h3>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <Card>
                        <CardBody>
                            <div style={{ maxWidth: 260, marginBottom: 16 }}>
                                <MaterialTextField
                                    name="select"
                                    select
                                    label="Status"
                                    value={status}
                                    onChange={(e) => onFilterChange(e.target.value as ReportStatus | '')}
                                >
                                    <MaterialFormOption value="">All</MaterialFormOption>
                                    {(Object.keys(STATUS_LABELS) as ReportStatus[]).map((s) => (
                                        <MaterialFormOption key={s} value={s}>
                                            {STATUS_LABELS[s]}
                                        </MaterialFormOption>
                                    ))}
                                </MaterialTextField>
                            </div>

                            {isLoading ? (
                                <div className="d-flex justify-content-center">
                                    <LoadingSpinner />
                                </div>
                            ) : reports.length === 0 ? (
                                <p style={{ color: '#999999', padding: '24px 0' }}>
                                    {status ? `No ${STATUS_LABELS[status as ReportStatus].toLowerCase()} reports.` : 'No reports yet.'}
                                </p>
                            ) : (
                                <>
                                    <TableWrap>
                                        <Table>
                                            <thead>
                                                <TableRow>
                                                    <TableCell>Reported</TableCell>
                                                    <TableCell>Reason</TableCell>
                                                    <TableCell>Message</TableCell>
                                                    <TableCell>Reported user</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Set status</TableCell>
                                                </TableRow>
                                            </thead>
                                            <TableBody>
                                                {reports.map((report) => (
                                                    <TableRow key={report.id}>
                                                        <TableCell>{formatDate(report.created_at)}</TableCell>
                                                        <TableCell>{REASON_LABELS[report.reason] ?? report.reason}</TableCell>
                                                        <TableCell style={{ maxWidth: 380 }}>
                                                            {/* The snapshot taken at report time — the live message may
                                                                since have been edited or deleted. */}
                                                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                {report.messageSnapshot}
                                                            </div>
                                                            {report.notes && (
                                                                <div style={{ marginTop: 6, fontSize: 12, color: '#999999' }}>
                                                                    Reporter’s note: {report.notes}
                                                                </div>
                                                            )}
                                                        </TableCell>
                                                        <TableCell style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                                            {report.reportedUserId}
                                                        </TableCell>
                                                        <TableCell>
                                                            <span style={{ color: STATUS_COLOURS[report.status], fontWeight: 600 }}>
                                                                {STATUS_LABELS[report.status] ?? report.status}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell style={{ minWidth: 170 }}>
                                                            <MaterialTextField
                                                                name="select"
                                                                select
                                                                label=""
                                                                value={report.status}
                                                                disabled={isSaving}
                                                                onChange={(e) =>
                                                                    updateStatus({ reportId: report.id, status: e.target.value as ReportStatus })
                                                                }
                                                            >
                                                                {(Object.keys(STATUS_LABELS) as ReportStatus[]).map((s) => (
                                                                    <MaterialFormOption key={s} value={s}>
                                                                        {STATUS_LABELS[s]}
                                                                    </MaterialFormOption>
                                                                ))}
                                                            </MaterialTextField>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableWrap>

                                    <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            disabled={cursors.length === 0}
                                            onClick={() => setCursors((c) => c.slice(0, -1))}
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            // A null cursor means the last page. Paging on it would loop.
                                            disabled={!nextCursor}
                                            onClick={() => nextCursor && setCursors((c) => [...c, nextCursor])}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ContentReports;
